import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n, { LANGUAGES } from '../../i18n.js'
import api from '../../services/api'

/**
 * Choose one of the six UN languages.
 *
 * Two jobs, and the second is the one that matters: switch the page NOW, and remember the
 * choice on the person's record so that everything sent to them later — every email, every
 * answer from the assistant — is written in it too. A picker that only repaints the screen
 * teaches people the setting does not work, because their invoices keep arriving in English.
 *
 * The page switches before the save completes. If the save fails they still get the language
 * they asked for in this session; they just have to choose again next time, which is a far
 * smaller failure than blocking on the network to change a label.
 */
export default function LanguagePicker({ compact = false, onChanged }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(null)
  const ref = useRef(null)

  const current =
    LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ||
    LANGUAGES.find((l) => l.code === 'en')

  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const choose = async (code) => {
    setOpen(false)
    if (code === i18n.resolvedLanguage) return
    setSaving(code)
    await i18n.changeLanguage(code)          // the page turns immediately
    try {
      await api.put('/users/me/', { language: code })   // and it is remembered
      const stored = JSON.parse(localStorage.getItem('user') || '{}')
      localStorage.setItem('user', JSON.stringify({ ...stored, language: code }))
    } catch {
      // Signed out (the login page has no session yet) or offline. The language still changed;
      // i18next has cached it locally and it will be saved the next time they set it.
    } finally {
      setSaving(null)
      onChanged?.(code)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('actions.view', 'Language')}
        title={current.english}
        className={
          compact
            ? 'flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100'
            : 'flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50'
        }
      >
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
        </svg>
        <span>{current.label}</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50"
        >
          {LANGUAGES.map((l) => {
            const active = l.code === i18n.resolvedLanguage
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                onClick={() => choose(l.code)}
                dir={l.dir}
                className={`flex w-full items-center justify-between px-4 py-2 text-sm hover:bg-gray-100 ${
                  active ? 'text-blue-700 font-semibold' : 'text-gray-700'
                }`}
              >
                <span>{l.label}</span>
                <span className="text-[10px] uppercase tracking-wide text-gray-400" dir="ltr">
                  {saving === l.code ? '…' : l.code}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
